/**
 * Sidebar Navigation
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Layers,
  Send,
  GitBranch,
  Gauge,
  CalendarDays,
  Boxes,
  Landmark,
  Users,
  Shield,
  Key,
  FileText,
  Database,
  Settings,
  BookOpen,
  RefreshCw,
  Activity,
  Tag,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  MessageSquareText,
  Bot,
  Wallet,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks';
import { Tooltip } from '@/components/ui';
import { SidebarBrandBar } from '@/components/layouts/sidebar-easter-egg';
import { useSeedFolderItems } from '@/components/layouts/sidebar-seed-folders';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  /** Single permission or array – show if user has any */
  permission?: string | string[];
  role?: string;
  /** NavLink `end` — only match `href` exactly (no child paths) */
  end?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    label: 'SMSWiser',
    href: '/smswiser',
    icon: Layers,
    permission: ['drops.view', 'stats.phone'],
  },
  { label: 'Calendar', href: '/calendar', icon: CalendarDays, permission: 'drops.calendar' },
  {
    label: 'Campaigns',
    href: '/campaigns',
    icon: Layers,
    permission: 'flows.view',
    role: 'admin',
    end: true,
  },
  { label: 'Banks', href: '/banks', icon: Landmark, permission: 'banks.view' },
  { label: 'Drops', href: '/drops', icon: Send, permission: 'drops.view' },
  { label: 'Flows', href: '/flows', icon: GitBranch, permission: 'flows.view', role: 'admin' },
  { label: 'Optimiser', href: '/scheduler', icon: Gauge, permission: 'flows.view' },
  {
    label: 'Automation',
    href: '/admin/automation',
    icon: Bot,
    permission: 'drop_generator.view',
  },
];

const adminItems: NavItem[] = [
  { label: 'Users', href: '/admin/users', icon: Users, permission: 'users.view' },
  { label: 'Roles', href: '/admin/roles', icon: Shield, permission: 'roles.view' },
  { label: 'API Keys', href: '/admin/api-keys', icon: Key, permission: 'admin.api_keys' },
  { label: 'Audit Logs', href: '/admin/audit-logs', icon: FileText, permission: 'audit.view' },
  { label: 'SIEM', href: '/admin/siem', icon: Shield, role: 'admin' },
  {
    label: 'Creative AI',
    href: '/admin/creative-ai',
    icon: MessageSquareText,
    permission: ['campaign_texts.view', 'campaign_senders.manage'],
  },
  { label: 'AI Models', href: '/admin/ai-models', icon: Sparkles, permission: 'admin.ai_models' },
  { label: 'Infrastructure', href: '/admin/infrastructure', icon: Activity, permission: 'admin.infrastructure' },
  { label: 'API Credits', href: '/admin/api-credits', icon: Wallet, permission: 'admin.api_credits', role: 'admin' },
  { label: 'Documentation', href: '/docs', icon: BookOpen, role: 'admin' },
];

const smswiserAdminItems: NavItem[] = [
  { label: 'Catalog', href: '/admin/smswiser-catalog', icon: Layers, permission: 'smswiser.catalog' },
  { label: 'Catalog Data', href: '/admin/smswiser-catalog-data', icon: Database, permission: 'smswiser.catalog' },
  { label: 'Texts', href: '/admin/smswiser-texts', icon: FileText, permission: 'admin.texts' },
  { label: 'Fetch Leads Count', href: '/admin/fetch-leads-counts', icon: RefreshCw, permission: 'smswiser.catalog' },
  { label: 'Sources', href: '/admin/smswiser-sources', icon: Database, permission: 'smswiser.sources' },
  { label: 'Config', href: '/admin/smswiser-config', icon: Settings, permission: 'smswiser.config' },
  {
    label: 'Tag Rules',
    href: '/admin/tag-rules',
    icon: Tag,
    permission: [
      'smswiser.list_tag_rules',
      'smswiser.text_tag_rules',
      'smswiser.gateway_tag_rules',
      'smswiser.hour_tag_rules',
      'smswiser.path_tag_rules',
      'smswiser.lander_tag_rules',
      'smswiser.offer_tag_rules',
      'smswiser.phone_tag_rules',
      'smswiser.drop_tag_rules',
      'smswiser.source_tag_rules',
    ],
  },
  { label: 'List Groups', href: '/admin/list-groups', icon: Boxes, permission: 'smswiser.list_groups' },
];

// The seed folders ("Offers/Network Rules", "Landers", "Flows/Paths" — the
// paths pull walks the whole flow tree) list one entry per tracker that can
// pull here: see sidebar-seed-folders.

export function Sidebar({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const { hasPermission, user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const location = useLocation();
  const {
    offers: offersFolderItems,
    landers: landersFolderItems,
    paths: pathsFolderItems,
  } = useSeedFolderItems();
  const isInSmswiserAdmin = smswiserAdminItems.some(
    (i) => location.pathname === i.href || location.pathname.startsWith(`${i.href}/`),
  );
  const [smswiserOpen, setSmswiserOpen] = useState(isInSmswiserAdmin);
  const isInOffers = offersFolderItems.some(
    (i) => location.pathname === i.href || location.pathname.startsWith(`${i.href}/`),
  );
  const [offersOpen, setOffersOpen] = useState(isInOffers);
  const isInLanders = landersFolderItems.some(
    (i) => location.pathname === i.href || location.pathname.startsWith(`${i.href}/`),
  );
  const [landersOpen, setLandersOpen] = useState(isInLanders);
  const isInPaths = pathsFolderItems.some(
    (i) => location.pathname === i.href || location.pathname.startsWith(`${i.href}/`),
  );
  const [pathsOpen, setPathsOpen] = useState(isInPaths);

  useEffect(() => {
    if (isInSmswiserAdmin) setSmswiserOpen(true);
  }, [isInSmswiserAdmin]);

  useEffect(() => {
    if (isInOffers) setOffersOpen(true);
  }, [isInOffers]);

  useEffect(() => {
    if (isInLanders) setLandersOpen(true);
  }, [isInLanders]);

  useEffect(() => {
    if (isInPaths) setPathsOpen(true);
  }, [isInPaths]);

  const renderNavItem = (item: NavItem, indent = false) => {
    if (item.role && item.role.toLowerCase() !== role) {
      return null;
    }

    if (item.permission) {
      const perms = Array.isArray(item.permission) ? item.permission : [item.permission];
      if (!perms.some((p) => hasPermission(p))) return null;
    }

    const link = (
      <NavLink
        key={item.href}
        to={item.href}
        end={item.end}
        className={({ isActive }) =>
          cn(
            'flex w-full items-center rounded-lg py-2 text-sm transition-colors',
            collapsed ? 'justify-center px-0' : 'gap-3 px-3',
            indent && !collapsed && 'pl-6',
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )
        }
      >
        <item.icon className={cn('h-4 w-4 flex-shrink-0', collapsed && 'h-5 w-5')} />
        {collapsed ? <span className="sr-only">{item.label}</span> : item.label}
      </NavLink>
    );

    if (!collapsed) return link;
    return (
      <Tooltip key={item.href} content={item.label} side="right" className="block w-full">
        {link}
      </Tooltip>
    );
  };

  const hasAnyPermission = (perms: string | string[] | undefined) => {
    if (!perms) return true;
    const arr = Array.isArray(perms) ? perms : [perms];
    return arr.some((p) => hasPermission(p));
  };

  const visibleAdminItems = adminItems.filter(
    (item) =>
      (!item.role || item.role.toLowerCase() === role) &&
      hasAnyPermission(item.permission),
  );

  const visibleSmswiserItems = smswiserAdminItems.filter((item) =>
    hasAnyPermission(item.permission),
  );

  const hasSmswiserAccess = visibleSmswiserItems.length > 0;

  const visibleOffersItems = offersFolderItems.filter((item) =>
    hasAnyPermission(item.permission),
  );
  const hasOffersAccess = visibleOffersItems.length > 0;

  const visibleLandersItems = landersFolderItems.filter((item) =>
    hasAnyPermission(item.permission),
  );
  const hasLandersAccess = visibleLandersItems.length > 0;

  const visiblePathsItems = pathsFolderItems.filter((item) =>
    hasAnyPermission(item.permission),
  );
  const hasPathsAccess = visiblePathsItems.length > 0;

  return (
    <aside
      className={cn(
        'hidden flex-shrink-0 border-r bg-card md:block transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="flex h-full flex-col">
        <SidebarBrandBar collapsed={collapsed} />

        {/* Navigation */}
        <nav
          className={cn(
            'sidebar-nav-hide-scrollbar flex-1 min-h-0 overflow-y-auto space-y-1',
            collapsed ? 'p-2' : 'p-4',
          )}
        >
          <div className="space-y-1">{navItems.map((item) => renderNavItem(item))}</div>

          {(visibleAdminItems.length > 0 ||
            hasSmswiserAccess ||
            hasOffersAccess ||
            hasLandersAccess ||
            hasPathsAccess) && (
            <>
              <div className="my-4 border-t" />
              {!collapsed ? (
                <p className="mb-2 px-3 text-xs font-semibold uppercase text-muted-foreground">
                  Admin
                </p>
              ) : null}
              <div className="space-y-1">
                {visibleAdminItems.map((item) => renderNavItem(item))}
                {hasSmswiserAccess &&
                  (collapsed ? (
                    visibleSmswiserItems.map((item) => renderNavItem(item))
                  ) : (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => setSmswiserOpen(!smswiserOpen)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          {smswiserOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                        <FolderOpen className="h-4 w-4" />
                        <span className="flex-1 text-left">SMSWiser</span>
                      </button>
                      {smswiserOpen && (
                        <div className="space-y-0.5 border-l border-border ml-2 pl-2 mt-0.5">
                          {visibleSmswiserItems.map((item) => renderNavItem(item, true))}
                        </div>
                      )}
                    </div>
                  ))}
                {hasOffersAccess &&
                  (collapsed ? (
                    visibleOffersItems.map((item) => renderNavItem(item))
                  ) : (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => setOffersOpen(!offersOpen)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          {offersOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                        <FolderOpen className="h-4 w-4" />
                        <span className="flex-1 text-left">Offers/Network Rules</span>
                      </button>
                      {offersOpen && (
                        <div className="space-y-0.5 border-l border-border ml-2 pl-2 mt-0.5">
                          {visibleOffersItems.map((item) => renderNavItem(item, true))}
                        </div>
                      )}
                    </div>
                  ))}
                {hasLandersAccess &&
                  (collapsed ? (
                    visibleLandersItems.map((item) => renderNavItem(item))
                  ) : (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => setLandersOpen(!landersOpen)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          {landersOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                        <FolderOpen className="h-4 w-4" />
                        <span className="flex-1 text-left">Landers</span>
                      </button>
                      {landersOpen && (
                        <div className="space-y-0.5 border-l border-border ml-2 pl-2 mt-0.5">
                          {visibleLandersItems.map((item) => renderNavItem(item, true))}
                        </div>
                      )}
                    </div>
                  ))}
                {hasPathsAccess &&
                  (collapsed ? (
                    visiblePathsItems.map((item) => renderNavItem(item))
                  ) : (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => setPathsOpen(!pathsOpen)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          {pathsOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                        <FolderOpen className="h-4 w-4" />
                        <span className="flex-1 text-left">Flows/Paths</span>
                      </button>
                      {pathsOpen && (
                        <div className="space-y-0.5 border-l border-border ml-2 pl-2 mt-0.5">
                          {visiblePathsItems.map((item) => renderNavItem(item, true))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </nav>

        {/* Settings */}
        <div className={cn('border-t', collapsed ? 'p-2' : 'p-4')}>
          {collapsed ? (
            <Tooltip content="Settings" side="right" className="block w-full">
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    'flex w-full items-center justify-center rounded-lg px-0 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                <Settings className="h-5 w-5" />
                <span className="sr-only">Settings</span>
              </NavLink>
            </Tooltip>
          ) : (
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              <Settings className="h-4 w-4" />
              Settings
            </NavLink>
          )}
        </div>
      </div>
    </aside>
  );
}
